import { useContext, useMemo } from 'react';

import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';

import { ExternalPatientContext } from '@/app/external/constants';
import { usePreferences } from '@/react-query/usePreferences';

import type { LatestImage } from '../../../../../@types/latestImages';
import type { ImageAnalyses } from '../../../../../@types/latestImagesV2';
import { selectTeethAtom } from '../../../../patientList/atoms';
import annotatorConstants from '../../../annotator.constants';
import { imageFilterAtom } from '../../../atoms';
import {
  casePresentationModeAtom,
  selectedFindingImageIdAtom,
  selectedFindingsAtom,
} from '../../../casePresentation/atoms';
import type { ImageObject } from '../../../types';

import { enabledImageTypesAtom, showingAllAtom } from './atoms';

const sortByConfidenceScores = (
  aConfidenceScore?: number,
  bConfidenceScore?: number,
) => {
  const aConfScore = aConfidenceScore ? aConfidenceScore : 0;
  const bConfScore = bConfidenceScore ? bConfidenceScore : 0;

  return aConfScore > bConfScore ? -1 : 0;
};

export const sortImages = (
  a: ImageObject | LatestImage,
  b: ImageObject | LatestImage,
  selectedTeeth: string[],
) => {
  if (
    (a?.fmxIndex || a?.fmxIndex === 0) &&
    (b?.fmxIndex || b?.fmxIndex === 0)
  ) {
    return a.fmxIndex < b.fmxIndex ? -1 : 0;
  }
  if (
    a?.detectedImageType === 'COLOR_IMAGE' &&
    b?.detectedImageType === 'COLOR_IMAGE' &&
    ((a?.photoView === 'intraoral' && b?.photoView === 'intraoral') ||
      (a?.photoView === 'extraoral' && b?.photoView === 'extraoral')) &&
    selectedTeeth?.length === 1
  ) {
    const aConfidenceScore = a?.models?.tooth_identification?.findings
      ?.filter((tooth) => tooth?.toothNumber?.toString() === selectedTeeth[0])
      .map((detectedTooth) => detectedTooth?.confidenceScore)?.[0];
    const bConfidenceScore = b?.models?.tooth_identification?.findings
      ?.filter((tooth) => tooth?.toothNumber?.toString() === selectedTeeth[0])
      .map((detectedTooth) => detectedTooth?.confidenceScore)?.[0];
    return sortByConfidenceScores(aConfidenceScore, bConfidenceScore);
  }

  if (
    a?.detectedImageType === 'COLOR_IMAGE' &&
    b?.detectedImageType === 'COLOR_IMAGE' &&
    a?.photoView !== 'outOfScope' &&
    b?.photoView !== 'outOfScope'
  ) {
    return a?.photoView === 'extraoral' ? -1 : 0;
  }
  if (
    a?.detectedImageType === 'COLOR_IMAGE' &&
    b?.detectedImageType === 'COLOR_IMAGE'
  ) {
    return a?.photoView === 'outOfScope' ? 0 : -1;
  }
  if (
    !a?.fmxIndex &&
    a?.fmxIndex !== 0 &&
    !b?.fmxIndex &&
    b?.fmxIndex !== 0 &&
    a?.detectedImageType !== 'COLOR_IMAGE' &&
    b?.detectedImageType !== 'COLOR_IMAGE'
  ) {
    return a?.detectedImageType === 'PANORAMIC' ? 0 : -1;
  }
  if (!a?.fmxIndex && a?.fmxIndex !== 0 && !b?.fmxIndex && b?.fmxIndex !== 0) {
    return a?.detectedImageType === 'COLOR_IMAGE' ? 0 : -1;
  }
  return !a?.fmxIndex && a?.fmxIndex !== 0 ? 0 : -1;
};

export const useExtraImages = () => {
  const { imageId: selectedImageId } = useParams<{
    id: string;
    imageId?: string;
  }>();

  const allImages = useGetFilteredImages({
    disableImageFilters: true,
    enableShowAll: true,
  });

  const shownImages = useGetFilteredImages({});
  const hiddenImages = allImages?.reduce((count, image) => {
    let newCount = count;
    if (!selectedImageId && (image.fmxIndex || image.fmxIndex === 0)) {
      return newCount;
    }
    if (
      !shownImages?.some((shownImage) => shownImage.imageId === image.imageId)
    ) {
      newCount += 1;
    }
    return newCount;
  }, 0);

  const outOfScope = shownImages?.reduce((count, image) => {
    let newCount = count;
    if (image.photoView === 'outOfScope') {
      newCount += 1;
    }
    return newCount;
  }, 0);

  return { hiddenImages, outOfScope };
};

export const useGetFilteredImages = ({
  ifCasePresentationMode,
  ifPresentSingleFindings,
  disableImageFilters,
  disableSelectTeeth,
  enableShowAll,
}: {
  ifCasePresentationMode?: boolean;
  ifPresentSingleFindings?: boolean;
  disableImageFilters?: boolean;
  disableSelectTeeth?: boolean;
  enableShowAll?: boolean;
}) => {
  const currentImageFilter = useAtomValue(imageFilterAtom);
  const isCasePresentationMode =
    useAtomValue(casePresentationModeAtom) || ifCasePresentationMode;
  const selectedFinding = useAtomValue(selectedFindingsAtom);
  const presentSingleFinding = selectedFinding || ifPresentSingleFindings;
  const selectedTeeth = useAtomValue(selectTeethAtom);
  const enabledImageTypes = useAtomValue(enabledImageTypesAtom);
  const selectFindingImageId = useAtomValue(selectedFindingImageIdAtom);
  const isShowingAll = useAtomValue(showingAllAtom) || enableShowAll;
  const { data: prefs } = usePreferences();

  const { id: selectedPatientId, imageId: selectedImageId } = useParams<{
    id: string;
    imageId?: string;
  }>();

  const patients = useSelector((state: any) => state.annotator?.patients) || {};
  const currentPatient = patients[selectedPatientId] || {};

  const currentPatientImages = currentPatient?.images;
  if (!currentPatientImages) {
    return;
  }

  const images: ImageObject[] | undefined = Object.values<ImageObject>(
    currentPatientImages,
  )
    .filter((image) => {
      const isValidFmx =
        enabledImageTypes?.some((type) => type === 'fmxPhotos') ||
        (image.imageGroup !== 'FMX' &&
          image.detectedImageType !== 'BITEWING' &&
          image.detectedImageType !== 'PERIAPICAL');

      const isValidFmxAsOther =
        enabledImageTypes?.some((type) => type === 'fmxAsOther') ||
        (image.imageGroup !== 'OTHER' &&
          image.detectedImageType !== 'BITEWING' &&
          image.detectedImageType !== 'PERIAPICAL');

      const isValidDental =
        enabledImageTypes?.some((type) => type === 'dentalPhotos') ||
        image.detectedImageType !== 'COLOR_IMAGE';

      const isValidPanoramic =
        enabledImageTypes?.some((type) => type === 'panoPhotos') ||
        image.detectedImageType !== 'PANORAMIC';

      const isValidOther =
        enabledImageTypes?.some((type) => type === 'otherPhotos') ||
        image.detectedImageType !== 'OTHER';

      return (
        (isShowingAll || image.photoView !== 'outOfScope') &&
        (disableImageFilters ||
          ((isValidFmx || isValidFmxAsOther) &&
            isValidDental &&
            isValidPanoramic &&
            isValidOther))
      );
    })
    .sort((a, b) => sortImages(a, b, selectedTeeth));

  // in the new footer, we always wanna know all image types available
  if (disableSelectTeeth) {
    return images;
  }

  // present finding
  if (isCasePresentationMode && presentSingleFinding) {
    return images.filter(
      (image) =>
        image.imageId === selectFindingImageId ||
        (image.models?.tooth_identification?.findings?.some(
          (tooth) =>
            String(tooth.toothNumber) === String(selectedFinding?.tooth),
        ) &&
          image.detectedImageType === 'COLOR_IMAGE'),
    );
  }

  // present tooth
  if (!!selectedTeeth && selectedTeeth.length && !disableSelectTeeth) {
    return images.filter((image) =>
      image.models?.tooth_identification?.findings?.some((tooth) =>
        selectedTeeth?.some((x) => x === tooth.toothNumber),
      ),
    );
  }

  // single image view or present all mode
  if (selectedImageId || isCasePresentationMode) {
    return images;
  }

  // FMX/BW view
  if (
    currentImageFilter === annotatorConstants.FMX ||
    currentImageFilter === annotatorConstants.BITEWING
  ) {
    return images.filter(
      (image) => image.imageGroup === 'OTHER' && image.imageId,
    );
  }

  return;
};

export const useTrackImageIndex = (id?: string | null) => {
  const images = useGetFilteredImages({});

  const { imageId: selectedImageId } = useParams<{
    id: string;
    imageId?: string;
  }>();

  if (!selectedImageId || !images || !images.length) {
    return;
  }

  const currentIndex = images?.findIndex((image) =>
    id ? image.imageId === id : image.imageId === selectedImageId,
  );

  return {
    next:
      currentIndex + 1 < images.length
        ? images[currentIndex + 1]?.imageId
        : images[0]?.imageId,
    skipNext:
      currentIndex + 2 < images.length
        ? images[currentIndex + 2]?.imageId
        : images[0]?.imageId,
    prev:
      currentIndex - 1 >= 0
        ? images[currentIndex - 1]?.imageId
        : images[images.length - 1]?.imageId,
    skipPrev:
      currentIndex - 2 >= 0
        ? images[currentIndex - 2]?.imageId
        : images[images.length - 1]?.imageId,
    currentIndex: currentIndex,
    totalImages: images.length,
  };
};

export const useGetCurrentImage = () => {
  const externalPatientContext = useContext(ExternalPatientContext);
  const { id: selectedPatientId, imageId: selectedImageId } = useParams<{
    id: string;
    imageId?: string;
  }>();
  const patientId = selectedPatientId ?? externalPatientContext.patientId;
  const imageId = selectedImageId ?? externalPatientContext.imageId;
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const selectedPhotoId = params.get('selected-photo');

  const patients = useSelector((state: any) => state.annotator?.patients) || {};
  const currentPatient = patients[patientId] || {};

  const currentPatientImages = currentPatient?.images;
  if (!currentPatientImages) {
    return { currentImage: undefined, selectedPhoto: undefined };
  }
  const imagesArray: ImageObject[] = currentPatient?.images
    ? Object.values(currentPatient.images)
    : [];

  const currentImage: ImageObject | undefined = imagesArray.find(
    (image) => image.imageId === imageId,
  );
  const selectedPhoto = imagesArray?.find(
    (image) => image.imageId && image.imageId === selectedPhotoId,
  );

  return { currentImage, selectedPhoto };
};

export const useGetImageTypes = () => {
  const images = useGetFilteredImages({
    disableImageFilters: true,
    disableSelectTeeth: true,
  });

  const imageTypesEnabled = {
    dentalPhotos: !!images?.filter(
      (image) => image.detectedImageType === 'COLOR_IMAGE',
    ).length,
    fmxPhotos: !!images?.filter((image) => image.imageGroup === 'FMX').length,
    panoPhotos: !!images?.filter(
      (image) => image.detectedImageType === 'PANORAMIC',
    ).length,
    otherPhotos: !!images?.filter(
      (image) => image.detectedImageType === 'OTHER',
    ).length,
    // these are fmx images that get dragged to the image grid
    fmxAsOther: !!images?.filter(
      (image) =>
        image.imageGroup === 'OTHER' &&
        (image.detectedImageType === 'BITEWING' ||
          image.detectedImageType === 'PERIAPICAL'),
    ).length,
  };

  return imageTypesEnabled;
};

export const getImageBrowserDisplayImages = ({
  numberOfImagesToDisplay,
  images,
  page,
}: {
  numberOfImagesToDisplay: number;
  images: ImageObject[] | LatestImage[] | undefined;
  page: number;
}) => {
  const firstImageIndex = (page - 1) * numberOfImagesToDisplay;

  if (!images || images.length < firstImageIndex) {
    return undefined;
  }

  const lastImageIndex =
    page * numberOfImagesToDisplay - 1 < images.length
      ? page * numberOfImagesToDisplay - 1
      : images.length - 1;

  return images.slice(firstImageIndex, lastImageIndex + 1);
};
